import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Check,
  Copy,
  Download,
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { AppSidebar } from "@/components/AppSidebar";
import { MobileNavTrigger } from "@/components/MobileTopBar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EditUserTab } from "@/components/admin/EditUserTab";
import AffiniaUserConfigImportLogTab from "@/components/admin/AffiniaUserConfigImportLogTab";
import { useToast } from "@/hooks/use-toast";
import { getUser } from "@/services/userLogin";
import {
  createUser,
  deleteUser,
  fetchDepartments,
  fetchGrades,
  fetchLocations,
  listUsers,
  resetUserPassword,
  updateUserRole,
  type AdminUser,
  type CreateUserPayload,
} from "@/services/adminApi";
import { downloadAffiniaUsersSsoConfigTemplate } from "@/services/affiniaUsersSsoConfigApi";
import AffiniaUsersSsoConfigImportModal from "@/components/admin/AffiniaUsersSsoConfigImportModal";
import { MultiSelect } from "@/components/MultiSelect";
import {
  ACCESS_LABELS,
  ALL_ACCESS,
  applyAllAccessExclusive,
  ROLE_LABELS,
  USER_ROLES,
  type UserRoleValue,
} from "@/lib/adminConstants";

const USERS_QUERY_KEY = ["admin", "users"] as const;

const AdminPage = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currentUserId = getUser()?.id != null ? String(getUser()!.id) : null;

  // Kept in the URL (?tab=...), not plain component state, so a full page
  // refresh reopens the same tab (e.g. "Edit user") instead of resetting to
  // "Users".
  const ADMIN_TAB_VALUES = ["users", "edit", "import-log"] as const;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: (typeof ADMIN_TAB_VALUES)[number] = (
    ADMIN_TAB_VALUES as readonly string[]
  ).includes(tabParam ?? "")
    ? (tabParam as (typeof ADMIN_TAB_VALUES)[number])
    : "users";
  const setTab = (next: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", next);
      return params;
    });
  };
  const [editUserId, setEditUserId] = useState<string>("");
  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AdminUser | null>(null);
  const [pendingReset, setPendingReset] = useState<AdminUser | null>(null);
  const [createdCreds, setCreatedCreds] = useState<{ email: string; password: string } | null>(
    null,
  );
  const [resetCreds, setResetCreds] = useState<{ email: string; password: string } | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const {
    data: users = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({ queryKey: USERS_QUERY_KEY, queryFn: listUsers });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });

  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: (_data, payload) => {
      setAddOpen(false);
      setCreatedCreds({ email: payload.email, password: payload.password });
      invalidate();
    },
    onError: (e: Error) =>
      toast({ variant: "destructive", title: "Couldn’t create user", description: e.message }),
  });

  const handleDownloadTemplate = async () => {
    if (isDownloadingTemplate) return;
    setIsDownloadingTemplate(true);
    try {
      await downloadAffiniaUsersSsoConfigTemplate();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Couldn’t download the template",
        description: (e as Error).message,
      });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const deleteMutation = useMutation({
    mutationFn: (user: AdminUser) => deleteUser(user.id),
    onSuccess: (_d, user) => {
      toast({ title: `Deleted ${user.name}` });
      setPendingDelete(null);
      invalidate();
    },
    onError: (e: Error) =>
      toast({ variant: "destructive", title: "Couldn’t delete user", description: e.message }),
  });

  const resetMutation = useMutation({
    mutationFn: (user: AdminUser) => resetUserPassword(user.id),
    onSuccess: (data, user) => {
      setResetCreds({ email: user.email, password: data.temp_password });
      setPendingReset(null);
    },
    onError: (e: Error) => {
      toast({ variant: "destructive", title: "Couldn’t reset password", description: e.message });
      setPendingReset(null);
    },
  });

  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserRoleValue }) => updateUserRole(id, role),
    onSuccess: () => {
      toast({ title: "Role updated" });
      invalidate();
    },
    onError: (e: Error) => {
      toast({ variant: "destructive", title: "Couldn’t update role", description: e.message });
      invalidate();
    },
  });

  const adminCount = useMemo(() => users.filter((u) => u.role === "admin").length, [users]);

  return (
    <SidebarProvider>
      <div className="min-h-dvh flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0 h-dvh overflow-hidden">
          <header className="pt-safe flex shrink-0 items-center gap-2 border-b border-border px-2 py-2 sm:gap-3 sm:px-6 sm:py-3">
            <MobileNavTrigger />
            <div className="flex items-center gap-2.5 min-w-0">
              <ShieldCheck className="h-5 w-5 text-primary shrink-0" aria-hidden />
              <div className="min-w-0">
                <h1 className="text-base font-semibold leading-tight text-foreground">
                  User management
                </h1>
                <p className="text-xs text-muted-foreground truncate">
                  Manage users and permissions for Prism.
                </p>
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-auto p-4 sm:p-6">
            <div className="mx-auto max-w-5xl">
              <Tabs value={tab} onValueChange={setTab}>
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <TabsList>
                    <TabsTrigger value="users">Users</TabsTrigger>
                    <TabsTrigger value="edit">Edit</TabsTrigger>
                    <TabsTrigger value="import-log">Logs</TabsTrigger>
                  </TabsList>
                  {tab === "users" && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={handleDownloadTemplate}
                        disabled={isDownloadingTemplate}
                      >
                        {isDownloadingTemplate ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        {isDownloadingTemplate ? "Downloading…" : "Download Template"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => setBulkImportOpen(true)}
                      >
                        <UserPlus className="h-4 w-4" />
                        Bulk Import
                      </Button>
                      <Button onClick={() => setAddOpen(true)} className="gap-2">
                        <Plus className="h-4 w-4" />
                        Add user
                      </Button>
                    </div>
                  )}
                </div>

                <TabsContent value="users" className="space-y-4 focus-visible:outline-none">
                  {!isLoading && !isError && users.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      {users.length} {users.length === 1 ? "user" : "users"} · {adminCount}{" "}
                      {adminCount === 1 ? "admin" : "admins"}
                    </p>
                  )}

                  {isError ? (
                <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />
              ) : isLoading ? (
                <LoadingState />
              ) : users.length === 0 ? (
                <EmptyState onAdd={() => setAddOpen(true)} />
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/40 hover:bg-muted/40">
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="w-[140px]">Role</TableHead>
                        <TableHead>Permissions</TableHead>
                        <TableHead className="w-[140px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => {
                        const isSelf = currentUserId != null && user.id === currentUserId;
                        return (
                          <TableRow key={user.id}>
                            <TableCell className="font-medium text-foreground">
                              <span className="flex items-center gap-2">
                                {user.name}
                                {isSelf && (
                                  <Badge variant="outline" className="text-[10px] font-normal">
                                    You
                                  </Badge>
                                )}
                              </span>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{user.email}</TableCell>
                            <TableCell>
                              <Select
                                value={user.role}
                                disabled={isSelf || roleMutation.isPending}
                                onValueChange={(role) =>
                                  roleMutation.mutate({ id: user.id, role: role as UserRoleValue })
                                }
                              >
                                <SelectTrigger className="h-8" aria-label={`Role for ${user.name}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {USER_ROLES.map((role) => (
                                    <SelectItem key={role} value={role}>
                                      {ROLE_LABELS[role]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <PermissionSummary user={user} />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-muted-foreground hover:text-accent-foreground"
                                  title="Edit user"
                                  aria-label={`Edit ${user.name}`}
                                  onClick={() => {
                                    setEditUserId(user.id);
                                    setTab("edit");
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-muted-foreground hover:text-accent-foreground"
                                  disabled={resetMutation.isPending}
                                  title="Reset password"
                                  aria-label={`Reset password for ${user.name}`}
                                  onClick={() => setPendingReset(user)}
                                >
                                  <KeyRound className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-muted-foreground hover:text-destructive"
                                  disabled={isSelf}
                                  title={isSelf ? "You can’t remove yourself" : "Remove user"}
                                  aria-label={`Remove ${user.name}`}
                                  onClick={() => setPendingDelete(user)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
                </TabsContent>

                <TabsContent value="edit" className="focus-visible:outline-none">
                  <EditUserTab
                    users={users}
                    isLoading={isLoading}
                    isError={isError}
                    onRetry={() => refetch()}
                    selectedId={editUserId}
                    onSelectedIdChange={setEditUserId}
                  />
                </TabsContent>

                <TabsContent value="import-log" className="focus-visible:outline-none">
                  <AffiniaUserConfigImportLogTab />
                </TabsContent>

              </Tabs>
            </div>
          </main>
        </div>
      </div>

      <AddUserDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSubmit={(payload) => createMutation.mutate(payload)}
        isSubmitting={createMutation.isPending}
      />

      <AffiniaUsersSsoConfigImportModal open={bulkImportOpen} onOpenChange={setBulkImportOpen} />

      <CredentialsDialog creds={createdCreds} onClose={() => setCreatedCreds(null)} />

      <CredentialsDialog
        creds={resetCreds}
        onClose={() => setResetCreds(null)}
        title="Password reset"
        description="Share this temporary password securely. It’s shown only now, and the user will be asked to set a new one at next sign-in."
      />

      <AlertDialog open={!!pendingReset} onOpenChange={(o) => !o && setPendingReset(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset password for {pendingReset?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This replaces {pendingReset?.email}’s current password with a new temporary one.
              They’ll be asked to set a new password at next sign-in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={resetMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (pendingReset) resetMutation.mutate(pendingReset);
              }}
            >
              {resetMutation.isPending ? "Resetting…" : "Reset password"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {pendingDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes {pendingDelete?.email} and revokes their access to Prism.
              This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (pendingDelete) deleteMutation.mutate(pendingDelete);
              }}
            >
              {deleteMutation.isPending ? "Removing…" : "Remove user"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
};

function PermissionSummary({ user }: { user: AdminUser }) {
  const summarize = (values: string[], singular: string, plural: string) => {
    if (values.length === 0) return null;
    if (values.includes(ALL_ACCESS)) return `All ${plural}`;
    return `${values.length} ${values.length === 1 ? singular : plural}`;
  };
  const parts = [
    summarize(user.emp_dept_permission, "department", "departments"),
    summarize(user.emp_office_location_permission, "location", "locations"),
    summarize(user.emp_grade_permission, "grade", "grades"),
  ].filter((p): p is string => p !== null);
  if (parts.length === 0) {
    return <span className="text-sm text-muted-foreground">None</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {parts.map((label) => (
        <Badge key={label} variant="secondary" className="font-normal">
          {label}
        </Badge>
      ))}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="rounded-lg border border-border divide-y divide-border">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="ml-auto h-8 w-24" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
      <div className="mb-4 rounded-full bg-secondary p-3">
        <Users className="h-6 w-6 text-secondary-foreground" aria-hidden />
      </div>
      <h2 className="text-base font-semibold text-foreground">No users yet</h2>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        Add the first account so people can sign in to Prism.
      </p>
      <Button onClick={onAdd} className="mt-5 gap-2">
        <UserPlus className="h-4 w-4" />
        Add user
      </Button>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message?: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-destructive/30 bg-destructive/5 py-16 text-center">
      <AlertCircle className="mb-3 h-6 w-6 text-destructive" aria-hidden />
      <h2 className="text-base font-semibold text-foreground">Couldn’t load users</h2>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        {message || "Something went wrong reaching the server."}
      </p>
      <Button variant="outline" onClick={onRetry} className="mt-5">
        Try again
      </Button>
    </div>
  );
}

function CredentialsDialog({
  creds,
  onClose,
  title = "User created",
  description = "Share these sign-in details securely. The password is shown only now — once you close this, it can’t be retrieved.",
}: {
  creds: { email: string; password: string } | null;
  onClose: () => void;
  title?: string;
  description?: string;
}) {
  const [copied, setCopied] = useState<"password" | "both" | null>(null);

  const copy = async (text: string, which: "password" | "both") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <Dialog open={!!creds} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
              <Check className="h-4 w-4 text-primary" aria-hidden />
            </span>
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Email</Label>
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
              {creds?.email}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Temporary password</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 select-all rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-sm tracking-tight">
                {creds?.password}
              </code>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => creds && copy(creds.password, "password")}
                title="Copy password"
                aria-label="Copy password"
              >
                {copied === "password" ? (
                  <Check className="h-4 w-4 text-primary" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() =>
              creds && copy(`Email: ${creds.email}\nPassword: ${creds.password}`, "both")
            }
          >
            {copied === "both" ? "Copied" : "Copy both"}
          </Button>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface AddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: CreateUserPayload) => void;
  isSubmitting: boolean;
}

function generatePassword(length = 16): string {
  const groups = [
    "abcdefghijkmnpqrstuvwxyz",
    "ABCDEFGHJKLMNPQRSTUVWXYZ",
    "23456789",
    "!@#$%^&*-_+?",
  ];
  const all = groups.join("");
  const pick = (set: string) => set[randInt(set.length)];
  const chars = groups.map(pick);
  while (chars.length < length) chars.push(pick(all));
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

function randInt(max: number): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0] % max;
}

const BLANK_FORM = {
  username: "",
  email: "",
  password: "",
  role: "user" as UserRoleValue,
  emp_dept: "",
  emp_office: "",
  emp_grade: "",
  is_employee: false,
  emp_dept_permission: [] as string[],
  emp_office_location_permission: [] as string[],
  emp_grade_permission: [] as string[],
};

function parseList(value: string): string[] {
  return value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

function AddUserDialog({ open, onOpenChange, onSubmit, isSubmitting }: AddUserDialogProps) {
  const [form, setForm] = useState(BLANK_FORM);
  const [touched, setTouched] = useState(false);

  const { data: departments = [] } = useQuery({
    queryKey: ["admin", "departments"],
    queryFn: fetchDepartments,
  });
  const { data: locations = [] } = useQuery({
    queryKey: ["admin", "locations"],
    queryFn: fetchLocations,
  });
  const { data: grades = [] } = useQuery({
    queryKey: ["admin", "grades"],
    queryFn: fetchGrades,
  });
  const deptOptions = useMemo(() => [ALL_ACCESS, ...departments], [departments]);
  const locationOptions = useMemo(() => [ALL_ACCESS, ...locations], [locations]);
  const gradeOptions = useMemo(() => [ALL_ACCESS, ...grades], [grades]);

  // "All access" (*) is mutually exclusive with specific values.
  const setPerm = (field: keyof typeof BLANK_FORM, next: string[]) => {
    setForm((prev) => ({
      ...prev,
      [field]: applyAllAccessExclusive(next, prev[field] as string[]),
    }));
  };

  useEffect(() => {
    if (open) {
      setForm(BLANK_FORM);
      setTouched(false);
    }
  }, [open]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const errors = {
    username: form.username.trim() ? "" : "Name is required.",
    email: !form.email.trim() ? "Email is required." : emailValid ? "" : "Enter a valid email.",
  };
  const isValid = !errors.username && !errors.email;

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
  };

  const handleSubmit = () => {
    setTouched(true);
    if (!isValid) return;
    onSubmit({
      username: form.username.trim(),
      email: form.email.trim(),
      password: generatePassword(),
      role: form.role,
      emp_dept: parseList(form.emp_dept),
      emp_office: parseList(form.emp_office),
      emp_grade: form.emp_grade.trim() || null,
      is_employee: form.is_employee,
      emp_dept_permission: form.emp_dept_permission,
      emp_office_location_permission: form.emp_office_location_permission,
      emp_grade_permission: form.emp_grade_permission,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription>
            Create a sign-in account and choose which departments and offices it can see. A
            temporary password is generated and shown once after the user is created.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4 py-2"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Field label="Name" htmlFor="au-name" error={touched ? errors.username : ""}>
            <Input
              id="au-name"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="Jane Cooper"
              autoComplete="off"
            />
          </Field>

          <Field label="Email" htmlFor="au-email" error={touched ? errors.email : ""}>
            <Input
              id="au-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="jane@firm.co.uk"
              autoComplete="off"
            />
          </Field>

          <Field label="Role" htmlFor="au-role">
            <Select
              value={form.role}
              onValueChange={(role) => setForm({ ...form, role: role as UserRoleValue })}
            >
              <SelectTrigger id="au-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {USER_ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <fieldset className="space-y-4 rounded-lg border border-border p-4">
            <legend className="px-1 text-xs font-semibold text-muted-foreground">
              Employee details
            </legend>

            <Field
              label="Department"
              htmlFor="au-dept"
              hint="Comma-separated for more than one."
            >
              <Input
                id="au-dept"
                value={form.emp_dept}
                onChange={(e) => setForm({ ...form, emp_dept: e.target.value })}
                placeholder="e.g. Audit/Accounts, Taxation"
              />
            </Field>

            <Field label="Office" htmlFor="au-office" hint="Comma-separated for more than one.">
              <Input
                id="au-office"
                value={form.emp_office}
                onChange={(e) => setForm({ ...form, emp_office: e.target.value })}
                placeholder="e.g. Ipswich, Colchester"
              />
            </Field>

            <Field label="Grade" htmlFor="au-grade">
              <Input
                id="au-grade"
                value={form.emp_grade}
                onChange={(e) => setForm({ ...form, emp_grade: e.target.value })}
                placeholder="e.g. Manager"
              />
            </Field>

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <Label htmlFor="au-employee" className="cursor-pointer">
                Is an employee
              </Label>
              <Switch
                id="au-employee"
                checked={form.is_employee}
                onCheckedChange={(is_employee) => setForm({ ...form, is_employee })}
              />
            </div>
          </fieldset>

          <fieldset className="space-y-4 rounded-lg border border-border p-4">
            <legend className="px-1 text-xs font-semibold text-muted-foreground">
              Data access
            </legend>
            <p className="text-xs text-muted-foreground">
              Which data this user can view. Choose "All access" or specific values; leaving one
              empty means no access.
            </p>

            <Field label="Departments" htmlFor="au-dept-perm" hint="All access, or specific departments. Empty means no access.">
              <MultiSelect
                id="au-dept-perm"
                options={deptOptions}
                selected={form.emp_dept_permission}
                onChange={(next) => setPerm("emp_dept_permission", next)}
                labels={ACCESS_LABELS}
                placeholder="No access"
              />
            </Field>

            <Field label="Locations" htmlFor="au-location-perm" hint="All access, or specific locations. Empty means no access.">
              <MultiSelect
                id="au-location-perm"
                options={locationOptions}
                selected={form.emp_office_location_permission}
                onChange={(next) => setPerm("emp_office_location_permission", next)}
                labels={ACCESS_LABELS}
                placeholder="No access"
              />
            </Field>

            <Field label="Grades" htmlFor="au-grade-perm" hint="All access, or specific grades. Empty means no access.">
              <MultiSelect
                id="au-grade-perm"
                options={gradeOptions}
                selected={form.emp_grade_permission}
                onChange={(next) => setPerm("emp_grade_permission", next)}
                labels={ACCESS_LABELS}
                placeholder="No access"
              />
            </Field>
          </fieldset>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating…" : "Create user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export default AdminPage;
